import serviceContainer from 'src/ServiceContainer';
import {
  receivedVisualizeMacroData,
  requestVisualizeData,
  receivedVisualizeData,
  requestVisualizeGraphData,
  receiveVisualizeGraphData,
  receiveError,
} from './Visualize.slice';

import { ASSORTMENT } from 'src/utils/Domain/Constants';
import type { AppThunkDispatch } from 'src/store.types';
import type { AppState } from 'src/reducers';
import { WrapperOwnProps } from './Visualize.container';
import { processApiParams } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import { isArray, isEmpty, isNil, mapValues } from 'lodash';
import { ListDataApi } from 'configuration-schemas/confdefnView';
import { ListDataOptions, BasicPivotItem } from 'src/worker/pivotWorker.types';
import coalesce from 'src/utils/Functions/Coalesce';
import { ComponentErrorType } from '../ErrorBoundary/ErrorBoundary.slice';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';

export function fetchVisualizeData(memberId: string, ownProps: WrapperOwnProps) {
  const service = serviceContainer.pivotService;
  return (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const viewState = getState().pages.assortmentBuild.visualize;
    const viewDefs = viewState.vizViewDefns;

    if (isNil(viewDefs)) return;

    const dataApi = (viewDefs.graphs as any).dataApi as ListDataApi;
    const processedDataApi = processApiParams(dataApi, ownProps.selectedItemPreviewData);
    dispatch(requestVisualizeData(memberId));

    return Promise.all([
      service.listData(processedDataApi.defnId, ASSORTMENT, processedDataApi.params),
      service.listData('AssortmentBuildVisualizeMacro', ASSORTMENT, { topMembers: memberId }),
    ]).then((visualizeData) => {
      dispatch(receivedVisualizeData(visualizeData[0]));
      dispatch(receivedVisualizeMacroData(visualizeData[1].flat));
    });
  };
}

/**
 * Fetches the MetricLineGraph data for Visualize, mirroring `fetchConfigurableGridGraphData`.
 * The graph config is already loaded in `vizViewDefns.graphs`, so only the graph data + categories
 * are fetched here; the graph is a presentational component fed from redux. Visualize passes no
 * `timeDataIndex`, so there is no group-by dimension to resolve.
 */
export function fetchVisualizeLineGraphData(selectedId?: string[]) {
  const service = serviceContainer.pivotService;
  return async (dispatch: AppThunkDispatch, getState: () => AppState): Promise<void> => {
    dispatch(requestVisualizeGraphData(selectedId ? { selectedId } : undefined));

    const viewState = getState().pages.assortmentBuild.visualize;
    const configResp = viewState.vizViewDefns?.graphs;
    const finalSelectedId = viewState.graphSelectedId ?? [];
    if (isNil(configResp)) {
      return;
    }

    try {
      const dataApi = (configResp as any).dataApi;
      const componentScope: Record<string, any> = {
        topMembers: finalSelectedId,
      };
      const processedApi = processApiParams(dataApi, componentScope);
      // Preserve literal parameters that do not resolve in the component scope.
      const params: ListDataOptions = {
        ...mapValues(processedApi.params, (value, key) =>
          isArray(value) && isEmpty(value) ? dataApi.params[key] : value,
        ),
        topMembers: finalSelectedId.join(','),
      };
      const dataResp = await service.listData(processedApi.defnId, ASSORTMENT, { ...params });

      const xAxisDataIndex = coalesce(configResp.graphs?.dataIndex, 'week');
      const categories = dataResp.tree.map((x) => x[xAxisDataIndex!]);
      const data = configResp.graphs?.primary.map((config) =>
        (['left', 'right'] as const).map((key) => {
          const graphConfig = config[key];
          if (isNil(graphConfig)) {
            throw new Error('Graph config not found');
          }
          if (!isArray(graphConfig)) {
            return dataResp.tree
              .slice(0, categories.length)
              .map((dataItem) => ({ ...dataItem, y: dataItem[graphConfig.dataIndex] }));
          }
          return graphConfig.map((graphSeries) => {
            return dataResp.tree
              .slice(0, categories.length)
              .map((dataItem) => ({ ...dataItem, y: dataItem[graphSeries.dataIndex] }));
          });
        }),
      );

      dispatch(receiveVisualizeGraphData({ data: (data ?? []) as BasicPivotItem[][][], categories }));
    } catch (error: any) {
      dispatch(
        receiveError({
          type: ComponentErrorType.data,
          message: (error as Error)?.message,
          name: ConfDefnComponentType.visualize,
          stack: (error as Error)?.stack,
          issues: error,
          defnId: error.defnId,
        }),
      );
    }
  };
}
